import { Controller } from "@hotwired/stimulus";
import DOMPurify from "dompurify";
import { customMatcher, domFromHtml } from "../utils/select2_utils";

export default class extends Controller {
  static values = {
    type: { type: String, default: "select" }, // "select" or "tags"
    noResultsMsg: { type: String, default: "No results found" }
  };

  connect() {
    const el = this.element;

    let elementOptions = el.dataset.options ? JSON.parse(el.dataset.options) : el.dataset;
    let defaultOptions = this.getDefaultOptions(el);
    const options = { ...elementOptions, ...defaultOptions };

    if (!this.isSelect2Initialized(el)) {
      $(el).select2(options);

      $(el).on("select2:unselecting", function (e) {
        var self = $(this);
        setTimeout(function () {
          self.select2("close");
        }, 0);
      });

      $(el).on("select2:select select2:clear select2:unselect", function () {
        const event = new Event("change", { bubbles: true }); // fire a native event
        this.dispatchEvent(event);
      });

      $(el).on("select2:select", this.handleSelect.bind(this));
    }
  }

  disconnect() {
    if (this.isSelect2Initialized(this.element)) {
      $(this.element).select2("destroy");
    }
  }

  isSelect2Initialized(el) {
    return $(el).data("select2") ? true : false;
  }

  getDefaultOptions(el) {
    let options = {
      minimumResultsForSearch: 5,
      templateResult: (item) => {
        // Given an item, determine if html has been provided, otherwise fall back to a standard template
        if (item.loading || item.id === "") return item.text;

        const html = item.result_html || item.element?.dataset?.resultHtml || standardResultTemplate(item);
        return domFromHtml(html, ["span", "i", "kbd", "small", "div", "br", "strong", "img"]);
      },
      templateSelection: (item) => {
        // Given an item, determine if html has been provided, otherwise fall back to a standard template
        if (item.loading || item.id === "") return item.text;

        const html = item.selection_html || item.element?.dataset?.selectionHtml || standardSelectionTemplate(item);
        return domFromHtml(html, ["span", "i", "small"]);
      },
      language: {
        noResults: () => {
          return DOMPurify.sanitize(this.noResultsMsgValue);
        },
      },
      matcher: customMatcher,
    };

    if (this.typeValue === "tags") {
      options.tags = true;
      options.minimumResultsForSearch = 0;
      options.multiple = el.multiple;
      options.tokenSeparators = [";"];

      if (!el.multiple) {
        options.allowClear = true;
      }
    }

    return options;
  }

  handleSelect() {
    if (this.element.dataset.closeOnSelect === "false") {
      this.clearSearchInput();
    }
  }

  clearSearchInput() {
    const searchInput = this.element.nextSibling.querySelector(".select2-search__field");

    if (searchInput !== undefined && searchInput !== null) {
      searchInput.value = " ";
    }
  }
}

// Standard selection template for select2 items
function standardSelectionTemplate(item) {
  const ds = item.element?.dataset || {};
  const icon = item.icon ?? ds.icon;
  const text = item.text ?? "";

  const iconHtml = icon ? `<i class="${icon} mr-3" aria-hidden="true"></i>` : "";
  const html = `<span>${iconHtml} ${text}</span>`;
  return html
}

// Standard result template for select2 items
function standardResultTemplate(item) {
  const ds = item.element?.dataset || {};
  const icon = item.icon ?? ds.icon;
  const subtitle = item.subtitle ?? ds.subtitle;
  const text = item.text ?? "";

  // if neither icon nor subtitle provided, let caller fall back to plain text
  if (!icon && !subtitle) {
    return `<div>${text}</div>`;
  }

  const iconHtml = icon ? `<i class="${icon} mr-3" aria-hidden="true"></i>` : "";
  const subtitleHtml = subtitle ? `<small class="d-block text-muted">${subtitle}</small>` : "";

  const html = `
    <div class="d-flex align-items-baseline">
      ${iconHtml}
      <div>
        ${text}
        ${subtitleHtml}
      </div>
    </div>
  `;

  return html;
}
